import classNames from 'classnames';
import { useLayoutEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { validatePresence } from 'shared';

import Clickable from 'ds/Clickable';
import Icon from 'ds/Icon';
import IconButton from 'ds/IconButton';
import Layout from 'ds/Layout';
import Modal from 'ds/Modal';
import Text from 'ds/Text';
import { useOnClickOutside } from 'ds/helpers';
import useUniqueId from 'ds/helpers/useUniqueId';
import TextInput, { Props as TextInputProps } from 'ds/inputs/TextInput';
import { InputImageIconProps } from 'ds/inputs/types';
import { FieldSize } from 'ds/types';
import { useListFocus } from 'hooks';
import useFieldSize from 'hooks/useFieldSize';

export type Props<T> = Pick<
  TextInputProps,
  | 'onChange'
  | 'label'
  | 'bigLabel'
  | 'size'
  | 'disabled'
  | 'onClear'
  | 'placeholder'
  | 'icon'
  | 'autoFocus'
  | 'onBlur'
  | 'isReadOnly'
> &
  InputImageIconProps & {
    results: Result<T>[];
    noMatchesItem?: JSX.Element;
    forceShowNoMatchesItem?: boolean;
    query?: string;
    onSelect: (value: T) => void;
    showResultsWhenEmpty?: boolean;
    maxItems?: number;
    maxVisibleItems?: number;
    highlightMatch?: boolean;
    required?: boolean;
    hideLabel?: boolean;
    error?: string;
    onCreateNew?: VoidFunction;
  } & ComboxBoxProps;

type ResultProps<T> = Pick<Props<T>, 'results' | 'onSelect' | 'highlightMatch'>;

export interface ComboxBoxProps {
  readOnlyHref?: string;
  isBottomSheet?: boolean;
  customMobileTextInput?: JSX.Element;
  setQuery?: (query: string) => void;
  setIsBottomSheetVisible?: (isVisible: boolean) => void;
  isBottomSheetVisible?: boolean;
}

export interface Result<T> {
  value: T;
  element: JSX.Element;
  disabled?: boolean;
}

const CLASSNAME = 'ComboBox';

const ComboBox = <T extends string | number>({
  label,
  hideLabel = false,
  query,
  onChange,
  onSelect,
  showResultsWhenEmpty = false,
  maxItems = 8,
  highlightMatch,
  disabled,
  onClear,
  placeholder,
  required = false,
  error,
  noMatchesItem,
  forceShowNoMatchesItem,
  maxVisibleItems,
  onCreateNew,
  isReadOnly,
  readOnlyHref,
  isBottomSheet,
  customMobileTextInput,
  setQuery,
  setIsBottomSheetVisible,
  isBottomSheetVisible,
  autoFocus,
  ...props
}: Props<T>) => {
  const scaledSize = useFieldSize();
  const [resultsVisible, setResultsVisible] = useState<boolean>(false);
  /** After user leaves the field (blur), show required error if still empty — not on first open. */
  const [showRequiredAfterBlur, setShowRequiredAfterBlur] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const size = props.size || scaledSize;
  const hasQuery = query && query.length > 0;
  let results = hasQuery ? props.results : showResultsWhenEmpty ? props.results : [];
  const history = useHistory();

  const [suppressValidation, setSuppressValidation] = useState<boolean>();
  const id = useUniqueId();
  /** Required blur error only after user clicked, typed, or used keys in the field (not open-drawer autofocus blur). */
  const hasEngagedRef = useRef(false);

  const showNoMatchesItem = (results.length === 0 || !!forceShowNoMatchesItem) && !!noMatchesItem;
  useOnClickOutside(ref, () => {
    setResultsVisible(false);
  });

  useLayoutEffect(() => {
    if (!autoFocus) return;
    ref.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [autoFocus]);

  const requiredError =
    required && showRequiredAfterBlur && (query == null || query.trim() === '')
      ? validatePresence({ fieldName: label ?? '', value: query ?? '', required: true }).error
      : undefined;

  const textInput = (
    <TextInput
      {...props}
      label={label}
      hideLabel={hideLabel}
      value={query}
      onChange={params => {
        hasEngagedRef.current = true;
        onChange(params);
        if (!resultsVisible) {
          setResultsVisible(true);
        }
      }}
      size={size}
      onFocus={() => {
        setResultsVisible(true);
        setShowRequiredAfterBlur(false);
      }}
      disabled={disabled}
      onClear={onClear}
      placeholder={placeholder}
      autoFocus={autoFocus}
      required={required}
      suppressValidation={suppressValidation}
      onBlur={params => {
        const willSetRequired = !suppressValidation && required && !params.value?.trim() && hasEngagedRef.current;
        if (willSetRequired) {
          setShowRequiredAfterBlur(true);
        }
        props.onBlur?.(params);
      }}
      onKeyDown={ev => {
        if (ev.key !== 'Tab') {
          hasEngagedRef.current = true;
        }
        if (ev.key === 'Tab') {
          setResultsVisible(false);
        }
      }}
      isReadOnly={isReadOnly}
      error={error ?? requiredError}
    />
  );

  const resultList = (
    <ResultList
      results={results.slice(0, maxItems)}
      noMatchesItem={noMatchesItem}
      showNoMatchesItem={showNoMatchesItem}
      onSelect={result => {
        setSuppressValidation(false);
        onSelect(result);
        setResultsVisible(false);
      }}
      onCreateNew={onCreateNew}
      onMouseDown={() => {
        setSuppressValidation(true);
      }}
      maxVisibleItems={maxVisibleItems}
      size={size}
      highlightMatch={highlightMatch}
      query={query}
      divId={id}
      setResultsVisible={setResultsVisible}
      isVisible={resultsVisible}
      key={showNoMatchesItem.toString()}
      disableBoxShadow={isBottomSheet}
    />
  );

  if (isBottomSheet && setIsBottomSheetVisible && isBottomSheetVisible != null) {
    return (
      <>
        <Clickable onClick={() => setIsBottomSheetVisible(true)}>
          <Layout
            align="center"
            gap={8}
            height={48}
            borderColor="gray-50"
            borderWidth={1}
            borderRadius={1000}
            paddingX={16}
          >
            <Icon name="search" size="md" />
            <Text size="body-sm">Add products</Text>
          </Layout>
        </Clickable>
        <Modal
          overflow="visible"
          bottomSheetIsRounded
          header={
            <Layout
              width="100%"
              height={84}
              borderBottom
              align="center"
              justify="flex-start"
              direction="row"
              paddingX={24}
              position="relative"
            >
              <IconButton
                name="leftChevron"
                size="md"
                type="noBackground"
                onClick={() => {
                  setQuery?.('');
                  setIsBottomSheetVisible(false);
                }}
              />
              {customMobileTextInput || textInput}
              <Clickable onClick={() => setQuery?.('')}>
                <Layout position="absolute" right={24} top={0} bottom={0} align="center" justify="center">
                  <Icon name="clear" size="lg" color="gray-400" />
                </Layout>
              </Clickable>
            </Layout>
          }
          isVisible={isBottomSheetVisible}
          onClose={() => {
            setIsBottomSheetVisible(false);
            setResultsVisible(false);
          }}
          showCloseButton={false}
        >
          <Layout marginTop={24}>{resultList}</Layout>
        </Modal>
      </>
    );
  }

  return (
    <div
      id={id}
      className={classNames(CLASSNAME, `is-size-${size}`, { 'is-link': readOnlyHref })}
      ref={ref}
      onPointerDownCapture={() => {
        hasEngagedRef.current = true;
      }}
      {...(readOnlyHref && isReadOnly
        ? {
            role: 'button',
            onClick: () => {
              readOnlyHref.startsWith('http') ? window.open(readOnlyHref, '_blank') : history.push(readOnlyHref);
            }
          }
        : {})}
    >
      {textInput}
      {resultList}
    </div>
  );
};

interface ResultListProps<T> extends ResultProps<T> {
  size: FieldSize;
  query?: string;
  onMouseDown: VoidFunction;
  isVisible?: boolean;
  noMatchesItem?: JSX.Element;
  showNoMatchesItem: boolean;
  onCreateNew?: VoidFunction;
  setResultsVisible: (isVisible: boolean) => void;
  divId: string;
  maxVisibleItems?: number;
  disableBoxShadow?: boolean;
}

const ResultList = <T extends string | number>({
  results,
  size,
  onSelect,
  onMouseDown,
  highlightMatch,
  query,
  isVisible,
  noMatchesItem,
  onCreateNew,
  showNoMatchesItem,
  setResultsVisible,
  maxVisibleItems,
  divId,
  disableBoxShadow
}: ResultListProps<T>) => {
  const [focusedIndex] = useListFocus({
    divId,
    size: results.length || (showNoMatchesItem ? 1 : 0),
    handleEnter: index => {
      if (!isVisible) return;
      if (showNoMatchesItem && onCreateNew) {
        const result = results[index];
        if (result && !result.disabled) {
          onSelect(result.value);
        } else {
          onCreateNew();
        }
      } else {
        const result = results[index];

        if (!result) return;

        if (result.disabled) return;

        onSelect(result.value);
      }

      setResultsVisible(false);
    }
  });

  return (
    <div
      className={classNames('ComboBox-results', { 'is-visible': isVisible })}
      style={{ boxShadow: disableBoxShadow ? 'none' : undefined }}
    >
      <ul
        style={{
          listStyleType: 'none',
          padding: 0,
          margin: 0,
          height: '100%',
          maxHeight: maxVisibleItems ? maxVisibleItems * 50 : undefined,
          overflowY: 'auto'
        }}
      >
        {results.map(({ element, value, disabled }, index) => (
          <ResultItem
            key={value}
            element={element}
            focused={index === focusedIndex}
            value={value}
            query={query}
            onSelect={onSelect}
            onMouseDown={onMouseDown}
            results={results}
            size={size}
            highlightMatch={highlightMatch}
            disabled={disabled}
          />
        ))}
        {showNoMatchesItem && (
          <li
            className={classNames('ComboBox-resultItem', {
              'is-not-creatable': !onCreateNew,
              'is-focused': focusedIndex === 0
            })}
            onClick={onCreateNew}
            key="noMatchesItem"
          >
            {noMatchesItem}
          </li>
        )}
      </ul>
    </div>
  );
};

interface ResultItemProps<T> extends ResultProps<T>, Pick<Props<T>, 'size'>, Result<T> {
  size: FieldSize;
  focused: boolean;
  query?: string;
  onMouseDown: VoidFunction;
}

const ResultItem = <T extends string | number>({
  value,
  focused,
  onSelect,
  disabled,
  // NOTE (Mark): Using this function interferes with onClickOutside in Modals
  // onMouseDown,
  element
}: ResultItemProps<T>) => {
  return (
    <li
      className={classNames('ComboBox-resultItem', { 'is-focused': focused, 'is-disabled': disabled })}
      key={value}
      onClick={() => {
        if (disabled) return;

        onSelect(value);
      }}
    >
      {element}
    </li>
  );
};

export default ComboBox;
